import React from 'react';
import Field from '../../Field';
import CancellationNotices from './CancellationNotices';
import SendButton from './SendButton';
import { clampAmountDecimals } from '../../../domain/money';
import { useCurrency } from '../../../context/LocaleContext';
import { attendanceOptionLabel } from '../../../domain/lessonAttendance';
import s from './attendance.module.css';
import { DecimalInput } from '../../RegionInputs';

// The status select shows each option's billing consequence inline: two are
// surprising — "Absent, Issue Make-Up Credit" bills the family, and so does
// "Teacher Cancelled, Issue Make-Up Credit" — and a teacher should find that
// out at the moment of choosing rather than on an invoice.
//
// The list is `statuses` from `useAttendanceForm`: the core's
// `attendanceStatusesFor` under MMS's three gates (#303, #351, #317) — the
// make-up balance against this lesson's length (eleven rows when it covers it,
// eight when not, #406), the category's `Allow make-up credits…` checkbox (six
// when off), and the event's `This event requires a make-up credit` (nine,
// withdrawing `Present` and `Present, Late`). The matrix is `statuses_for` in
// `core/src/attendance.rs`. This component does no filtering of its own.
//
// Nor billing arithmetic (#362): `st.billable` is the lesson's answer, since
// `statuses_for` folds the event's `requiresMakeupCredit` in, so `Unrecorded`
// on a flagged event arrives reading `Not Billable`, as MMS captions it.
//
// The row's words are `attendanceOptionLabel` (src/domain/lessonAttendance.js),
// the spending half said as plainly as the issuing half.

export default function AttendanceTab({
  status, onStatusChange, statuses = [], consequence, showPrice,
  priceText, onPriceChange, priceError = null,
  paidAtLesson, onPaidChange,
  paidText, onPaidTextChange, paidError = null,
  paymentNote, onPaymentNoteChange,
  contacts = [], me = null,
  receiptTo, onReceiptToChange,
  receiptCcMe, onReceiptCcMeChange,
  showCancellationNotices = false, noticeAlreadyQueued = false,
  notices = {}, noticeGroups = [], onToggleNoticeChannel,
  cancellationNote = '', onCancellationNoteChange, reminderWarning = null,
  // The Email Receipt and Send Cancellation Notice buttons (#732):
  // `sends.receipt` and `sends.notice` from useAttendanceForm, and `onSend`,
  // which is handed the kind. Save sends neither.
  sends = {}, onSend,
  noticeQueuedText = 'A cancellation notice for this lesson has already been queued.',
}) {
  // Both money fields truncate a third decimal rather than rounding (#308):
  // `from_decimal` rounds half-up, so `50.999` would become $51.00 on the
  // screen that writes the ledger charge and the payment.
  // `clampAmountDecimals` is MMS's own editor on its Add Payment drawer
  // (#246): the third keystroke is swallowed. The value reaches
  // `useAttendanceForm` already truncated.
  const currency = useCurrency();
  const clamp = (v) => clampAmountDecimals(v, currency);

  return (
    <>
      <Field label="Status">
        <select className={s.input} value={status} onChange={(e) => onStatusChange(e.target.value)}>
          {statuses.map((st) => (
            <option key={st.id} value={st.id}>{attendanceOptionLabel(st)}</option>
          ))}
        </select>
      </Field>

      <div className={showPrice ? s.consequence : s.consequenceMuted}>{consequence}</div>

      {/* Progressive disclosure, as in MMS: price and payment appear only once
          a status has been chosen. Unrecorded is billable, but MMS shows no
          price against it — the teacher has not decided yet. */}
      {showPrice ? (
        <>
          <div className={s.priceBlock}>
            {/* `priceError` is computed in useAttendanceForm.js, which has the
                studio's currency (#171): a price past MAX_MONEY_MINOR is refused
                here rather than sinking the whole attendance batch. */}
            <Field label="Lesson Price" error={priceError}>
              <DecimalInput
                className={s.input}
                value={priceText} onChange={(e) => onPriceChange(clamp(e.target.value))}
              />
            </Field>
          </div>

          <label className={s.checkRow}>
            <input
              type="checkbox" checked={paidAtLesson}
              onChange={(e) => onPaidChange(e.target.checked)}
            />
            <span>Student paid at lesson</span>
          </label>

          {paidAtLesson ? (
            <>
              {/* Its own amount, defaulted to the lesson price but editable —
                  a teacher handed $20 for a $30 lesson records $20, and the
                  remaining $10 stays on the family's balance. */}
              <div className={s.priceBlock}>
                {/* `paidError` is computed in useAttendanceForm.js (#174): a
                    payment past MAX_MONEY_MINOR is refused here rather than
                    sinking the whole attendance batch. */}
                <Field label="Paid at Lesson" error={paidError}>
                  <DecimalInput
                    className={s.input}
                    value={paidText} onChange={(e) => onPaidTextChange(clamp(e.target.value))}
                  />
                </Field>
              </div>
              <div className={s.noteBlock}>
                <Field label="Payment Note" hint="Optional — appears on the transaction">
                  <textarea
                    rows={3} className={s.input}
                    value={paymentNote} onChange={(e) => onPaymentNoteChange(e.target.value)}
                  />
                </Field>

                {/* One recipient, defaulting to none — MMS's select, with the
                    family's contacts under a "No Email Receipt" default. A
                    family with no email address has nothing to pick, so the
                    control says that instead of offering an empty dropdown.

                    Sent from the Email Receipt button under it, not from Save
                    (#732) — a knowing difference from MMS, whose Save sends the
                    receipt picked here. The button saves first, so a receipt is
                    only ever for a payment that saved. */}
                <Field label="Email Receipt">
                  {contacts.length ? (
                    <select
                      className={s.input}
                      value={receiptTo}
                      onChange={(e) => onReceiptToChange(e.target.value)}
                    >
                      <option value="">No Email Receipt</option>
                      {contacts.map((c) => (
                        <option key={c.address} value={c.address}>{c.name || c.address}</option>
                      ))}
                    </select>
                  ) : (
                    <div className={s.muted}>
                      No email address on this family, so there is nobody to send it to.
                    </div>
                  )}
                </Field>

                {receiptTo ? (
                  <>
                    <label className={s.checkRow}>
                      <input
                        type="checkbox" checked={receiptCcMe}
                        onChange={(e) => onReceiptCcMeChange(e.target.checked)}
                        disabled={!me}
                      />
                      <span>CC me in this email</span>
                    </label>
                  </>
                ) : null}

                {contacts.length ? (
                  <SendButton send={sends.receipt} onSend={() => onSend?.('receipt')} />
                ) : null}
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {/* Calling the lesson off (#187). At the foot of the tab, below the
          money: "who do we tell" is the last decision, as in the delete
          dialog. */}
      {showCancellationNotices ? (
        <CancellationNotices
          notices={notices}
          groups={noticeGroups}
          onToggleChannel={onToggleNoticeChannel}
          note={cancellationNote}
          onNoteChange={onCancellationNoteChange}
          reminderWarning={reminderWarning}
          footer={<SendButton send={sends.notice} onSend={() => onSend?.('notice')} />}
        />
      ) : null}

      {/* Already asked for, so there is nothing left to tick: the queue drops
          a repeat request by id, so the boxes would do nothing. */}
      {noticeAlreadyQueued ? (
        <div className={s.noticeSent}>{noticeQueuedText}</div>
      ) : null}
    </>
  );
}
